import { z } from 'zod'
import { optionsGroupsMainSchema } from './optionGroups'
import { localImageSchema, priceSchema } from './common'

export const SizeId = {
  LARGE: 'LARGE',
  MEDIUM: 'MEDIUM',
  SMALL: 'SMALL',
  NONE: 'NONE'
} as const

const sizeUnion = z.union([
  z.literal(SizeId.LARGE),
  z.literal(SizeId.MEDIUM),
  z.literal(SizeId.SMALL),
  z.literal(SizeId.NONE)
])

export type SizeType = z.infer<typeof sizeUnion>

export const productLiteSchema = z.object({
  identifier: z.string(),
  name: z.string(),
  imageUrl: localImageSchema, // will come loyaltyImg on loyaltyTiers, else imageUrl of product
  description: z.string().optional().default(''),
  price: priceSchema,
  unifiedPrice: priceSchema.optional(),
  originalPrice: priceSchema.optional(),
  size: sizeUnion.optional(),
  combo: z.boolean().default(false),
  barcode: z.string().optional(),
  genericName: z.string().optional(),
  dark: z.boolean().optional(),
  // loyalty
  points: z.number().optional(),
  loyaltyOfferId: z.string().optional(),
  // filters
  categories: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? [])
})

export type ProductLite = z.infer<typeof productLiteSchema>

export const ProductType = {
  CROSSSELLING: 'crossSelling',
  CROSSSELLING_NBO: 'crossSellingNBO'
} as const

const productTypeUnion = z
  .union([z.literal(ProductType.CROSSSELLING_NBO), z.literal(ProductType.CROSSSELLING)])
  .optional()

export type ProductTypeEnum = z.infer<typeof productTypeUnion>

export const productLiteCategorized = productLiteSchema.extend({
  idCategory: z.string().toLowerCase(),
  productType: productTypeUnion
})

export type ProductLiteCategorized = z.infer<typeof productLiteCategorized>

const sizeSchema = z.object({
  id: sizeUnion,
  code: z.string(),
  idCategory: z.string().toLowerCase(),
  price: priceSchema,
  imageUrl: localImageSchema,
  name: z.string(),
  combo: z.boolean().default(false)
})

export type ProductSize = z.infer<typeof sizeSchema>

export const productDto = productLiteCategorized.extend({
  idCategory: z.string(),
  optionsGroups: z.array(optionsGroupsMainSchema),
  sizes: z.array(sizeSchema).optional(),
  // loyalty
  loyaltyImg: localImageSchema
})

export type Product = z.infer<typeof productDto>

export const crossSellingProductsDto = z.array(productLiteCategorized)

export type ProductExtraInfo = {
  identifier: string
  unitPriceAmount: number
  unifiedPriceAmount?: number
  computedUnitPrice: number
  customization: string
  isLoyalty: boolean
}
